<template>
  <div>
    <fullscreen-dialog
      v-model="model"
      :title="employeeFullName + ' - ' + $t('credit_transactions')"
      :subtitle="companyName"
      icon="mdi-credit-card"
      hide-footer
      hide-padding
      @close="$emit('close')"
    >
      <template v-slot:filters>
        <!-- Desktop filters -->
        <page-toolbar v-if="!device.isMobile" :searchable="false">
          <template v-slot:filters>
            <v-col cols="6" lg="auto">
              <date-picker v-model="dateRange" color="white" hide-details />
            </v-col>
            <v-col cols="6" lg="auto">
              <autocomplete
                v-model="filterTypes"
                :items="filterCreditTypes"
                :label="$t('type')"
                item-text="text"
                item-value="value"
                color="white"
                hide-details
                multiple
                select-all
                clearable
              />
            </v-col>
            <v-col cols="6" lg="auto">
              <text-field
                v-model="search"
                kind="search"
                :label="$t('search')"
                color="white"
                hide-details
              />
            </v-col>

            <v-col>
              <v-row justify="end" no-gutters>
                <excel-button
                  @click="exportExcel"
                  :loading="isExcelDownloading"
                  class="mr-2"
                ></excel-button>
                <print-button @click="print" :loading="isPrinting"></print-button>
              </v-row>
            </v-col>
          </template>
        </page-toolbar>

        <!-- Mobile filters -->
        <template v-else>
          <mobile-filters
            :filters="mobileFilterConfig"
            @update:filter="handleFilterUpdate"
            @apply="onFiltersApply"
            @reset="resetFilters"
          >
            <template v-slot:actions>
              <print-button @click="print" />
              <excel-button @click="exportExcel" :loading="isExcelDownloading" />
            </template>
          </mobile-filters>
        </template>
      </template>

      <!-- Mobile View -->
      <template v-if="device.isMobile">
        <empty-state
          v-if="!loading && filteredTransactions.length === 0"
          icon="mdi-credit-card-off"
          :title="$t('no_transactions')"
          :subtitle="
            hasFilters ? $t('try_different_filters') : $t('no_credit_transactions_yet')
          "
        >
          <primary-button @click="dialog = true">
            <v-icon left>mdi-plus</v-icon>
            {{ $t("tooltip_add_credit") }}
          </primary-button>
        </empty-state>

        <mobile-data-cards
          v-else
          :headers="mobileHeaders"
          :items="filteredTransactions"
          :loading="loading"
          item-key="id"
          show-filters
          :fields-per-row="null"
          class="mt-3"
          :show-row-number="false"
          :bold-fields="['amount']"
        >
          <template v-slot:item.type="{ item }">
            <v-chip x-small :color="getTypeColor(item.type)" dark>
              {{ formatType(item.type) }}
            </v-chip>
          </template>
          <template v-slot:item.amount="{ item }">
            {{ (item.amount / 100).toFixed(2) }}
          </template>
          <template v-slot:item.balance_after="{ item }">
            {{ (item.balance_after / 100).toFixed(2) }}
          </template>
          <template v-slot:item.created_at="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>
        </mobile-data-cards>

        <div class="fixed-bottom-button">
          <v-btn color="primary" block @click="dialog = true">
            <v-icon class="mr-2">mdi-plus</v-icon>
            {{ $t("tooltip_add_credit") }}
          </v-btn>
        </div>
      </template>

      <!-- Desktop View -->
      <template v-else>
        <empty-state
          v-if="!loading && filteredTransactions.length === 0"
          icon="mdi-credit-card-off"
          :title="$t('no_transactions')"
          :subtitle="
            hasFilters ? $t('try_different_filters') : $t('no_credit_transactions_yet')
          "
        >
          <primary-button @click="dialog = true">
            <v-icon left>mdi-plus</v-icon>
            {{ $t("tooltip_add_credit") }}
          </primary-button>
        </empty-state>

        <v-data-table
          v-else
          :headers="headers"
          :items="filteredTransactions"
          :loading="loading"
          :items-per-page="10"
          class="ordera-box-shadow striped-table"
        >
          <template v-slot:item.type="{ item }">
            <v-chip small :color="getTypeColor(item.type)" dark>
              {{ formatType(item.type) }}
            </v-chip>
          </template>

          <template v-slot:item.amount="{ item }">
            {{ (item.amount / 100).toFixed(2) }}
          </template>

          <template v-slot:item.balance_after="{ item }">
            {{ (item.balance_after / 100).toFixed(2) }}
          </template>

          <template v-slot:item.created_at="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>
        </v-data-table>
      </template>

      <!-- Add Credit Dialog -->
      <add-credit-dialog
        v-model="dialog"
        :employee-id="employee.id"
        @saved="fetchTransactions"
      />
    </fullscreen-dialog>

    <!-- FAB for adding credit (desktop only) -->
    <v-tooltip left v-if="!device.isMobile">
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          v-if="model"
          fab
          fixed
          bottom
          right
          color="primary"
          large
          class="credit-fab primary-gradient"
          v-bind="attrs"
          v-on="on"
          @click="dialog = true"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </template>
      <span>{{ $t("tooltip_add_credit") }}</span>
    </v-tooltip>
  </div>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import axios from "@/plugins/axios";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import MobileFilters from "@/components/global/MobileFilters.vue";
import AddCreditDialog from "./AddCreditDialog.vue";

export default {
  name: "CreditTransactions",
  components: {
    FullscreenDialog,
    PageToolbar,
    EmptyState,
    MobileDataCards,
    MobileFilters,
    AddCreditDialog,
  },
  props: {
    employee: {
      type: Object,
      default: () => ({}),
    },
  },
  mixins: [hasModel, hasPrinting, hasExcel],
  data() {
    return {
      transactions: [],
      loading: false,
      dialog: false,
      search: "",
      dateRange: this.getDefaultDateRange(),
      filterTypes: ["topup", "admin_adjust", "promo"],
    };
  },
  mounted() {
    this.fetchTransactions();
  },
  methods: {
    getDefaultDateRange() {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const formatDate = (d) => d.toISOString().split("T")[0];
      return [formatDate(firstDayOfMonth), formatDate(today)];
    },
    async fetchTransactions() {
      this.loading = true;
      try {
        const res = await axios.get("/credits/history?user_id=" + this.employee.id);
        this.transactions = res.data.data || res.data;
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    formatDateTime(dateString) {
      if (!dateString) return "";
      // Handle format "DD-MM-YYYY HH:mm:ss"
      const parsed = this.parseDateTime(dateString);
      if (parsed) {
        return this.localeDate(parsed, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      // Fallback - try to parse as-is
      return this.localeDate(dateString);
    },
    formatType(type) {
      const types = {
        topup: this.$t("topup"),
        admin_adjust: this.$t("admin_adjust"),
        promo: this.$t("promo"),
        purchase: this.$t("purchase"),
        refund: this.$t("refund"),
      };
      return types[type] || type;
    },
    getTypeColor(type) {
      const colors = {
        topup: "success",
        admin_adjust: "info",
        promo: "purple",
        purchase: "orange",
        refund: "grey",
      };
      return colors[type] || "grey";
    },
    parseDateTime(dateString) {
      if (!dateString) return null;
      // Handle format "DD-MM-YYYY HH:mm:ss"
      const parts = dateString.split(" ");
      if (parts.length === 2) {
        const dateParts = parts[0].split("-");
        if (dateParts.length === 3) {
          // Convert DD-MM-YYYY to YYYY-MM-DD
          const isoDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}T${parts[1]}`;
          return new Date(isoDate);
        }
      }
      // Fallback - try to parse as-is
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? null : date;
    },
    print() {
      const printHeaders = this.headers.map((h) => ({
        text: h.text,
        value: h.value,
      }));
      const printItems = this.filteredTransactions.map((t) => ({
        ...t,
        type: this.formatType(t.type),
        amount: (t.amount / 100).toFixed(2),
        balance_after: (t.balance_after / 100).toFixed(2),
        created_at: this.formatDateTime(t.created_at),
      }));
      this.printDocument({
        title: this.$t("credit_transactions"),
        subtitle: this.employeeFullName,
        tables: [
          {
            headers: printHeaders,
            items: printItems,
          },
        ],
      });
    },
    exportExcel() {
      const excelHeaders = this.headers.map((h) => ({
        text: h.text,
        value: h.value,
      }));
      const excelItems = this.filteredTransactions.map((t) => ({
        ...t,
        type: this.formatType(t.type),
        amount: (t.amount / 100).toFixed(2),
        balance_after: (t.balance_after / 100).toFixed(2),
        created_at: this.formatDateTime(t.created_at),
      }));
      this.downloadExcel({
        fileName: `${this.employeeFullName}_${this.$t("credit_transactions")}.xlsx`,
        sheets: [
          {
            name: this.$t("credit_transactions"),
            title: this.$t("credit_transactions"),
            subtitle: this.employeeFullName,
            headers: excelHeaders,
            items: excelItems,
          },
        ],
      });
    },
    handleFilterUpdate({ key, value }) {
      this[key] = value;
    },
    onFiltersApply() {
      // Filters are already reactive, no additional action needed
    },
    resetFilters() {
      this.dateRange = [];
      this.filterTypes = [];
      this.search = "";
    },
  },
  computed: {
    headers() {
      return [
        { text: "ID", value: "id", mobile: false },
        { text: this.$t("date"), value: "created_at", cols: 6 },
        { text: this.$t("type"), value: "type", cols: 6 },
        { text: this.$t("amount"), value: "amount", align: "right", cols: 6 },
        {
          text: this.$t("balance_after"),
          value: "balance_after",
          align: "right",
          cols: 6,
        },
        { text: this.$t("note"), value: "note", mobile: false },
      ];
    },
    mobileHeaders() {
      return this.headers.filter((h) => h.mobile !== false);
    },
    filterCreditTypes() {
      return [
        { text: this.$t("topup"), value: "topup" },
        { text: this.$t("admin_adjust"), value: "admin_adjust" },
        { text: this.$t("promo"), value: "promo" },
      ];
    },
    filteredTransactions() {
      let result = this.transactions;
      // Filter by types (array)
      if (this.filterTypes && this.filterTypes.length > 0) {
        result = result.filter((t) => this.filterTypes.includes(t.type));
      }

      // Filter by date range
      if (this.dateRange && this.dateRange.length === 2) {
        const fromDate = new Date(this.dateRange[0]);
        fromDate.setHours(0, 0, 0, 0);
        const toDate = new Date(this.dateRange[1]);
        toDate.setHours(23, 59, 59, 999);
        result = result.filter((t) => {
          const date = this.parseDateTime(t.created_at);
          if (!date) return true;
          return date >= fromDate && date <= toDate;
        });
      }

      // Filter by search term
      if (this.search && this.search.trim()) {
        const searchLower = this.translate(this.search.toLowerCase().trim());
        result = result.filter((t) => {
          const type = this.translate(this.formatType(t.type).toLowerCase());
          const note = this.translate((t.note || "").toLowerCase());
          const date = this.translate(this.formatDateTime(t.created_at).toLowerCase());
          const amount = (t.amount / 100).toFixed(2);
          const balanceAfter = (t.balance_after / 100).toFixed(2);
          const id = String(t.id);

          return (
            type.includes(searchLower) ||
            note.includes(searchLower) ||
            date.includes(searchLower) ||
            amount.includes(searchLower) ||
            balanceAfter.includes(searchLower) ||
            id.includes(searchLower)
          );
        });
      }

      return result;
    },
    employeeFullName() {
      const name = this.employee?.name || "";
      const lastname = this.employee?.lastname || "";
      return this.toLatin(`${name} ${lastname}`.trim());
    },
    companyName() {
      return this.toLatin(this.employee?.company?.name || "");
    },
    hasFilters() {
      return (
        (this.filterTypes && this.filterTypes.length > 0) ||
        this.search ||
        (this.dateRange && this.dateRange.length === 2)
      );
    },
    mobileFilterConfig() {
      return {
        dateRange: {
          type: "date",
          label: this.$t("date"),
          icon: "mdi-calendar",
          value: this.dateRange,
          chipText: this.formatDateRangeChip || this.$t("date"),
          visible: true,
          clearable: true,
        },
        filterTypes: {
          type: "autocomplete",
          label: this.$t("type"),
          icon: "mdi-tag",
          items: this.filterCreditTypes,
          value: this.filterTypes,
          multiple: true,
          chipText: this.formatTypesChip || this.$t("type"),
          visible: true,
          clearable: true,
        },
      };
    },
    formatDateRangeChip() {
      if (!this.dateRange || !this.dateRange[0]) return "";
      const formatDate = (date) => {
        const d = new Date(date);
        return `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1)
          .toString()
          .padStart(2, "0")}.`;
      };
      if (this.dateRange[0] === this.dateRange[1] || !this.dateRange[1]) {
        return formatDate(this.dateRange[0]);
      }
      return `${formatDate(this.dateRange[0])} - ${formatDate(this.dateRange[1])}`;
    },
    formatTypesChip() {
      if (!this.filterTypes || !this.filterTypes.length) return "";
      if (this.filterTypes.length === 1) {
        const type = this.filterCreditTypes.find((t) => t.value === this.filterTypes[0]);
        return type?.text || this.filterTypes[0];
      }
      return `${this.filterTypes.length} ${this.$t("type")}`;
    },
  },
};
</script>

<style scoped>
.headline {
  font-weight: 600;
}

.credit-fab {
  z-index: 1000 !important;
  margin-bottom: 16px !important;
}

.fixed-bottom-button {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px;
  background: white;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  z-index: 5;
}

.mobile-actions-bar {
  background-color: #f5f5f5;
}
</style>
